import type { JSX } from "react";
import { useMemo, useState } from "react";
import type {
  AnalyticsBucket,
  AnalyticsCounterKey,
  AnalyticsFieldKey,
  AnalyticsMoneyKey,
  AnalyticsSortKey,
  AnalyticsSource,
  AnalyticsStatusKey,
  SegmentDefinition,
} from "@adlywise/shared";
import { COUNTER_DIMENSIONS } from "@adlywise/shared";
import {
  buildAnalyticsCategories,
  type AnalyticsCategory,
  type AnalyticsFieldDescriptor,
} from "@/features/analytics/filterCategories";
import {
  AddRowButton,
  FieldName,
  FieldPicker,
  OrderedRows,
  fieldLabelIn,
  narrowCategories,
  type OrderedRow,
} from "./RowBuilder";

// Report configuration for the analytics aggregation report: the AnalyticsConfig
// model, the pure transforms the side panel applies to it, and the panel's
// Metrics and Group-by section bodies. The config drives (a) the aggregation
// request (source / groupBy / bucket / statuses / money / counters) and (b) the
// property set the Group-by and Filters sections offer (categoriesForConfig).
// The property picker is ORDERED — selection order IS the GROUP BY / ORDER BY
// order — so selected props render as an ordered, reorderable list.

// Response-side metric statuses. Each selected status becomes its own metric
// group ({total, devices}) in the results table. Wire values are the lowercase
// keys (the shared AnalyticsStatusKey enum); the label is what the UI shows.
// (requests source has no status — its single metric group is a fixed "requests".)
export type MetricStatus = AnalyticsStatusKey;

export const METRIC_STATUS_OPTIONS: { value: MetricStatus; label: string }[] = [
  { value: "bid", label: "Bid" },
  { value: "won", label: "Won" },
  { value: "impression", label: "Impressions" },
  // VAST player events: a view is playback started, a completed view is
  // playback to the end.
  { value: "view", label: "Views" },
  { value: "complete", label: "Completed views" },
  { value: "click", label: "Clicks" },
  { value: "loss", label: "Loss" },
  { value: "billable", label: "Billable" },
  { value: "nobid", label: "No-bid" },
  // Popunder lifecycle.
  { value: "visit", label: "Pop visits" },
  { value: "confirmed", label: "Pop confirmed visits" },
  { value: "conversion", label: "Pop conversions" },
];

// Default responses selection = the populated statuses.
export const DEFAULT_METRIC_STATUSES: MetricStatus[] = [
  "bid",
  "impression",
  "view",
];

export type AnalyticsConfig = {
  source: AnalyticsSource;
  // Ordered: order = GROUP BY order = ORDER BY order. MAY be empty (a pure
  // time series of one metric group per bucket).
  groupBy: AnalyticsFieldKey[];
  bucket: AnalyticsBucket;
  // Ordered response-side metric statuses. Only meaningful when
  // source === "responses"; ignored (fixed "requests" group) on requests.
  statuses: MetricStatus[];
  // Money columns. Responses only: adRequest carries no prices.
  money: AnalyticsMoneyKey[];
  // Exchange counter columns, read from the per-minute aggregate. Selecting any
  // of these restricts the grouping to provider / campaign / segment, because
  // those are the only dimensions the counters store.
  counters: AnalyticsCounterKey[];
  // Row order (ORDER BY). null = the default: time, then groupBy in order.
  // Once edited it is explicit, and groupBy changes are carried into it (see
  // withGroupBy). "bucket" is always in it.
  sortBy: AnalyticsSortKey[] | null;
  // Metric column order by MetricGroup key. null = metricGroupsFor's own order;
  // keys it does not list keep that order after the listed ones.
  metricOrder: string[] | null;
  // Where the Time column sits among the grouping columns (0 = first).
  // Presentation only, like metricOrder; clamped to the groupings there are.
  timeColumn: number;
};

export const createDefaultAnalyticsConfig = (): AnalyticsConfig => ({
  source: "requests",
  groupBy: [],
  bucket: "day",
  statuses: DEFAULT_METRIC_STATUSES,
  // Both default to empty: the report opens on the behaviour it had before these
  // existed, and a money or counter column appears only once asked for.
  money: [],
  counters: [],
  sortBy: null,
  metricOrder: null,
  timeColumn: 0,
});

// The ordered metric groups a given config produces — the single source of truth
// for the results-table columns and their order. requests → one fixed "requests"
// group; responses → one group per selected status, in selection order.
// A metric group is one or two table columns.
//   pair   — the original shape: `<key>_total` and `<key>_devices`.
//   single — one column named for the key. Money and counters are single: a
//            distinct-devices figure is meaningless for a sum of money, and the
//            counters carry no device dimension at all.
// `format` tells the table how to render it. Money and percent arrive as decimal
// STRINGS (the figures behind them are exact micro-dollar sums) and may be null
// for a ratio with no denominator.
export type MetricGroup = {
  key: string;
  label: string;
  kind: "pair" | "single";
  format: "count" | "money" | "percent";
};

export const metricGroupsFor = (
  config: Omit<AnalyticsConfig, "metricOrder"> &
    Partial<Pick<AnalyticsConfig, "metricOrder">>,
): MetricGroup[] => {
  const groups = defaultMetricGroups(config);
  const order = config.metricOrder;
  if (!order) return groups;
  const rank = (key: string): number => {
    const i = order.indexOf(key);
    return i < 0 ? order.length : i;
  };
  // Array.prototype.sort is stable, so unlisted keys keep the default order.
  return [...groups].sort((a, b) => rank(a.key) - rank(b.key));
};

const defaultMetricGroups = (
  config: Pick<AnalyticsConfig, "source" | "statuses" | "money" | "counters">,
): MetricGroup[] => {
  // A counter report is answered from the per-minute aggregate, which has no row
  // and no device to count, so it never carries the request/status pairs.
  if (config.counters.length > 0) {
    return config.counters.map((c) => ({
      key: c,
      label: COUNTER_OPTIONS.find((o) => o.value === c)?.label ?? c,
      kind: "single" as const,
      format: "count" as const,
    }));
  }

  if (config.source === "requests") {
    return [
      { key: "requests", label: "Requests", kind: "pair", format: "count" },
    ];
  }

  const labelOf = (s: MetricStatus): string =>
    METRIC_STATUS_OPTIONS.find((o) => o.value === s)?.label ?? s;

  return [
    ...config.statuses.flatMap((s): MetricGroup[] => {
      const group: MetricGroup = {
        key: s,
        label: labelOf(s),
        kind: "pair",
        format: "count",
      };
      // Views always travel with their VTR (views ÷ impressions × 100). The
      // API emits `vtr` whenever `view` is selected, whether or not
      // Impressions is also a selected metric.
      return s === "view"
        ? [
            group,
            { key: "vtr", label: "VTR", kind: "single", format: "percent" },
          ]
        : [group];
    }),
    ...config.money.map((money) => ({
      key: money,
      label: MONEY_OPTIONS.find((o) => o.value === money)?.label ?? money,
      kind: "single" as const,
      format: (money === "margin" ? "percent" : "money") as "money" | "percent",
    })),
  ];
};

// Human labels for each canonical field key. Must stay in sync with the shared
// AnalyticsFieldKeySchema (the enum is the source of truth for the keys). Used
// by AggregatedResultsTable for the group-column headers and as a fallback label
// in the property picker.
export const FIELD_LABELS: Record<AnalyticsFieldKey, string> = {
  country: "Country",
  region: "Region",
  city: "City",
  platform: "Ad source",
  deviceType: "Device type",
  channel: "Channel",
  kind: "Ad format",
  isPaid: "Paid inventory",
  livestream: "Livestream",
  gender: "Co-viewing gender",
  provider: "Provider / Publisher",
  source: "Request source",
  categories: "Content categories",
  // Exchange capture (migration 0033). "Ad format" is already taken by `kind`,
  // our own inventory classification, so the OpenRTB one is named for where it
  // comes from rather than colliding.
  siteDomain: "Site domain",
  appBundle: "App bundle",
  trafficType: "Traffic type",
  adFormat: "Creative format (RTB)",
  publisherId: "Publisher ID (SSP)",
  os: "OS",
  language: "Language",
  dealId: "Deal ID",
  campaign: "Campaign",
  segment: "Segment",
  creative: "Creative",
};

// Money columns. Responses only — adRequest carries no prices — so the panel
// hides them on the requests source rather than letting the request 400.
export const MONEY_OPTIONS: { value: AnalyticsMoneyKey; label: string }[] = [
  { value: "spend", label: "Spend (payout)" },
  { value: "revenue", label: "Revenue" },
  { value: "profit", label: "Profit" },
  { value: "ecpm", label: "eCPM" },
  { value: "margin", label: "Margin %" },
];

// Exchange counters. These read the per-minute aggregate, which is the ONLY
// source for a no-bid, a timeout, a throttle or a skipped DSP call: none of
// those produce a row in any table. The trade is that the counters store only
// provider and campaign, so selecting one restricts the grouping to
// those — the panel enforces that rather than letting the request 400.
export const COUNTER_OPTIONS: { value: AnalyticsCounterKey; label: string }[] =
  [
    { value: "sspRequests", label: "SSP requests" },
    { value: "sspThrottled", label: "Throttled (QPS)" },
    { value: "sspPaused", label: "Rejected (paused)" },
    { value: "sspFiltered", label: "Rejected (filters)" },
    { value: "sspWins", label: "Bids returned to SSP" },
    { value: "dspRequests", label: "DSP requests sent" },
    { value: "dspSkippedQps", label: "DSP skipped (QPS)" },
    { value: "dspResponses", label: "DSP responses" },
    { value: "dspNoBid", label: "DSP no-bid" },
    { value: "dspTimeouts", label: "DSP timeouts" },
    { value: "dspHttpErrors", label: "DSP HTTP errors" },
    { value: "invalidBids", label: "Invalid bids" },
    { value: "bidsBelowFloor", label: "Bids below floor" },
    { value: "validBids", label: "Valid bids" },
    { value: "dspWins", label: "DSP wins" },
    { value: "popRequests", label: "Pop requests" },
    { value: "popBids", label: "Pop bids" },
    { value: "popWins", label: "Pop wins (nurl)" },
    { value: "popBilled", label: "Pop billed (burl)" },
    { value: "popVisits", label: "Pop visits (admitted)" },
    { value: "popConfirmed", label: "Pop confirmed visits" },
    { value: "popConversions", label: "Pop conversions" },
    { value: "popDisabled", label: "Pop no-bid: provider disabled" },
    { value: "popNoMatch", label: "Pop no-bid: no matching campaign" },
    { value: "popZoneExcluded", label: "Pop no-bid: zone/site excluded" },
    { value: "popSegmentMiss", label: "Pop no-bid: no matching segment" },
    { value: "popExhausted", label: "Pop no-bid: budget/cap reached" },
    { value: "popBelowFloor", label: "Pop no-bid: below floor" },
    { value: "popIvt", label: "Pop no-bid: invalid traffic" },
    { value: "popNoRedis", label: "Pop no-bid: Redis unavailable" },
    { value: "popDeadline", label: "Pop no-bid: out of time" },
    { value: "popCappedAtBid", label: "Pop no-bid: user capped" },
    { value: "popError", label: "Pop bid errors" },
    { value: "popRegionUnknown", label: "Pop region unknown" },
    { value: "popVisitIvt", label: "Pop invalid visits" },
    { value: "popFpMismatch", label: "Pop fingerprint mismatches" },
    { value: "popPriceMismatch", label: "Pop notice price mismatches" },
    { value: "popWrongPurpose", label: "Pop wrong-purpose tokens" },
    { value: "popNoticeIpDenied", label: "Pop notices from denied IPs" },
    { value: "popNoticePersistFailed", label: "Pop notice write failures" },
    { value: "popVisitPersistFailed", label: "Pop visit write failures" },
    { value: "popPostbackRejected", label: "Pop postbacks rejected" },
  ];

// The dimensions the counters can break down by. DERIVED from the shared
// constant the backend validates against, not re-typed: a hand-kept copy drifts
// the moment one side gains or loses a dimension, and the symptom would be a
// property the panel offers and the API rejects.
export const COUNTER_DIMENSION_KEYS: AnalyticsFieldKey[] = [
  ...COUNTER_DIMENSIONS,
];

export const BUCKETS: { value: AnalyticsBucket; label: string }[] = [
  { value: "hour", label: "Hour" },
  { value: "day", label: "Day" },
  { value: "week", label: "Week (Mon–Sun)" },
  { value: "month", label: "Month" },
];

export const SOURCES: { value: AnalyticsSource; label: string }[] = [
  { value: "requests", label: "Ad requests" },
  { value: "responses", label: "Ad responses" },
];

// ── config transforms ───────────────────────────────────────────────────────
// Pure (config → config), so the panel stays a thin view and the rules can be
// tested without rendering.

// The properties a config can group AND filter by: every category of its source
// (Campaign — campaign/segment/creative live on adResponse — only on responses),
// narrowed to the counter dimensions while a counter metric is selected, because
// the per-minute aggregate stores nothing else and the API refuses the rest.
//
// Deliberately NOT narrowed by groupBy: a filter on a property the report does
// not break down by is an ordinary WHERE, and the aggregate endpoint answers it
// like any other (a request-side one on responses fires the same request_raw
// join a grouping would).
export const categoriesForConfig = (
  config: Pick<AnalyticsConfig, "source" | "counters">,
): AnalyticsCategory[] => {
  const all = buildAnalyticsCategories({
    source: config.source,
    includeCampaign: config.source === "responses",
  });
  if (config.counters.length === 0) return all;
  const allowed = new Set<string>(COUNTER_DIMENSION_KEYS);
  return all
    .map((category) => {
      const fields = category.fields.filter((f) => allowed.has(f));
      const fieldDescriptors: Record<string, AnalyticsFieldDescriptor> = {};
      for (const field of fields) {
        const descriptor = category.fieldDescriptors[field];
        if (descriptor) fieldDescriptors[field] = descriptor;
      }
      return { ...category, fields, fieldDescriptors };
    })
    .filter((category) => category.fields.length > 0);
};

const fieldsOf = (
  config: Pick<AnalyticsConfig, "source" | "counters">,
): Set<string> =>
  new Set(categoriesForConfig(config).flatMap((category) => category.fields));

// Conditions the report can actually carry, given its config. The filter lives
// in its own state, separate from `config`, so nothing in the config transforms
// can prune it -- and a condition the new config cannot express (a campaign
// filter after switching to requests, a country filter after selecting a
// counter) would make every run a 400 the user has no way to connect to the
// stale condition. Pruning only ever removes, so the same object comes back when
// nothing was dropped (no needless re-render). The user SEES the count drop,
// whereas a silently dropped filter returns a number for a question nobody asked.
export const pruneFilterForConfig = (
  value: SegmentDefinition,
  config: Pick<AnalyticsConfig, "source" | "counters">,
): SegmentDefinition => {
  const allowed = fieldsOf(config);
  const all = value.all ?? [];
  const kept = all.filter((condition) => allowed.has(condition.field));
  return kept.length === all.length ? value : { all: kept };
};

// Switching source drops any props no longer valid for it (response-side props
// on requests) so the request stays consistent.
export const withSource = (
  config: AnalyticsConfig,
  source: AnalyticsSource,
): AnalyticsConfig => {
  if (source === config.source) return config;
  const allowed = fieldsOf({ ...config, source });
  return withGroupBy(
    { ...config, source },
    config.groupBy.filter((key) => allowed.has(key)),
  );
};

// list[from] moved to index `to`; the same list when either is out of range.
export const moveItem = <T,>(
  list: readonly T[],
  from: number,
  to: number,
): T[] => {
  if (from === to || from < 0 || to < 0) return [...list];
  if (from >= list.length || to >= list.length) return [...list];
  const next = [...list];
  const [moved] = next.splice(from, 1);
  if (moved !== undefined) next.splice(to, 0, moved);
  return next;
};

// The ORDER BY the report runs with.
export const effectiveSortBy = (
  config: Pick<AnalyticsConfig, "groupBy" | "sortBy">,
): AnalyticsSortKey[] => config.sortBy ?? ["bucket", ...config.groupBy];

// Every groupBy change goes through here so an edited sort follows it: keys no
// longer grouped by leave the sort, newly grouped ones join at its end (the
// default puts every grouping in the sort).
const withGroupBy = (
  config: AnalyticsConfig,
  groupBy: AnalyticsFieldKey[],
): AnalyticsConfig => {
  if (config.sortBy === null) return { ...config, groupBy };
  const kept = config.sortBy.filter(
    (key) => key === "bucket" || groupBy.includes(key),
  );
  const added = groupBy.filter((key) => !config.groupBy.includes(key));
  return { ...config, groupBy, sortBy: [...kept, ...added] };
};

export const withBucket = (
  config: AnalyticsConfig,
  bucket: AnalyticsBucket,
): AnalyticsConfig => ({ ...config, bucket });

// At least one status must stay selected.
export const withStatusToggled = (
  config: AnalyticsConfig,
  status: MetricStatus,
): AnalyticsConfig => {
  if (!config.statuses.includes(status)) {
    return { ...config, statuses: [...config.statuses, status] };
  }
  if (config.statuses.length === 1) return config;
  return { ...config, statuses: config.statuses.filter((s) => s !== status) };
};

export const withMoneyToggled = (
  config: AnalyticsConfig,
  key: AnalyticsMoneyKey,
): AnalyticsConfig => ({
  ...config,
  money: config.money.includes(key)
    ? config.money.filter((m) => m !== key)
    : [...config.money, key],
});

// Selecting a counter changes where the whole report is answered from: the
// per-minute aggregate instead of the row tables. It stores only the
// COUNTER_DIMENSION_KEYS, so any finer grouping is dropped here rather than
// being sent and rejected. Money is cleared for the same reason: a different
// grain cannot share a row. The filter is pruned too, but by the page (see
// pruneFilterForConfig), since it is not part of `config`.
//
// `statuses` is deliberately NOT cleared. It must stay non-empty (the toggle
// refuses to remove the last one), so instead the status chips are hidden while
// a counter is selected and the request omits the field — which has the nice
// property that the previous status selection comes back untouched when the
// counter is deselected.
export const withCounterToggled = (
  config: AnalyticsConfig,
  key: AnalyticsCounterKey,
): AnalyticsConfig => {
  const counters = config.counters.includes(key)
    ? config.counters.filter((c) => c !== key)
    : [...config.counters, key];
  if (counters.length === 0) return { ...config, counters };
  const allowed = new Set<string>(COUNTER_DIMENSION_KEYS);
  return withGroupBy(
    { ...config, counters, money: [] },
    config.groupBy.filter((k) => allowed.has(k)),
  );
};

export const withFieldToggled = (
  config: AnalyticsConfig,
  key: AnalyticsFieldKey,
): AnalyticsConfig =>
  withGroupBy(
    config,
    config.groupBy.includes(key)
      ? config.groupBy.filter((k) => k !== key)
      : [...config.groupBy, key],
  );

export const withFieldMoved = (
  config: AnalyticsConfig,
  from: number,
  to: number,
): AnalyticsConfig => withGroupBy(config, moveItem(config.groupBy, from, to));

export const withSortMoved = (
  config: AnalyticsConfig,
  from: number,
  to: number,
): AnalyticsConfig => ({
  ...config,
  sortBy: moveItem(effectiveSortBy(config), from, to),
});

// Time cannot leave the sort.
export const withSortRemoved = (
  config: AnalyticsConfig,
  key: AnalyticsSortKey,
): AnalyticsConfig =>
  key === "bucket"
    ? config
    : { ...config, sortBy: effectiveSortBy(config).filter((k) => k !== key) };

export const withSortAdded = (
  config: AnalyticsConfig,
  key: AnalyticsFieldKey,
): AnalyticsConfig => {
  const sortBy = effectiveSortBy(config);
  if (sortBy.includes(key) || !config.groupBy.includes(key)) return config;
  return { ...config, sortBy: [...sortBy, key] };
};

// The leading block of columns: Time among the groupings, at timeColumn.
export type DimensionColumn = "bucket" | AnalyticsFieldKey;

export const dimensionColumns = (
  config: Pick<AnalyticsConfig, "groupBy"> &
    Partial<Pick<AnalyticsConfig, "timeColumn">>,
): DimensionColumn[] => {
  const at = Math.min(
    Math.max(config.timeColumn ?? 0, 0),
    config.groupBy.length,
  );
  return [
    ...config.groupBy.slice(0, at),
    "bucket",
    ...config.groupBy.slice(at),
  ];
};

// Moving a column of that block moves Time or reorders the groupings (whose
// order is also the GROUP BY order, as in Group by).
export const withDimensionMoved = (
  config: AnalyticsConfig,
  from: number,
  to: number,
): AnalyticsConfig => {
  const next = moveItem(dimensionColumns(config), from, to);
  return {
    ...withGroupBy(
      config,
      next.filter((key): key is AnalyticsFieldKey => key !== "bucket"),
    ),
    timeColumn: next.indexOf("bucket"),
  };
};

// Metric columns reorder among themselves only (columns are always time, then
// groupings, then metrics); indexes are into metricGroupsFor(config).
export const withMetricMoved = (
  config: AnalyticsConfig,
  from: number,
  to: number,
): AnalyticsConfig => ({
  ...config,
  metricOrder: moveItem(
    metricGroupsFor(config).map((g) => g.key),
    from,
    to,
  ),
});

// How many metric columns the config asks for — the Metrics section's badge.
export const selectedMetricsCount = (config: AnalyticsConfig): number =>
  config.counters.length > 0
    ? config.counters.length
    : config.source === "requests"
      ? 1
      : config.statuses.length + config.money.length;

// ── section bodies ──────────────────────────────────────────────────────────

export const Segmented = <T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (next: T) => void;
  ariaLabel?: string;
}): JSX.Element => (
  <div
    role="group"
    aria-label={ariaLabel}
    className="inline-flex flex-wrap rounded-(--radius-md) bg-(--surface-2) p-0.5"
  >
    {options.map((opt) => (
      <button
        key={opt.value}
        type="button"
        onClick={() => onChange(opt.value)}
        aria-pressed={value === opt.value}
        className={`rounded-[calc(var(--radius-md)-2px)] px-3 py-1.5 text-[13px] font-medium transition-colors ${
          value === opt.value
            ? "bg-(--surface-1) text-(--text-primary) shadow-sm"
            : "text-(--text-secondary) hover:text-(--text-primary)"
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

const SectionTitle = ({
  title,
  hint,
}: {
  title: string;
  hint?: string;
}): JSX.Element => (
  <span className="text-[12px] font-semibold text-(--text-secondary)">
    {title}
    {hint ? (
      <span className="ml-1.5 font-normal text-(--text-muted)">{hint}</span>
    ) : null}
  </span>
);

// A toggleable pill; `order` (1-based) shows the selection position when the
// order is meaningful (statuses set the column order).
const Chip = ({
  label,
  isSelected,
  order,
  onClick,
}: {
  label: string;
  isSelected: boolean;
  order?: number;
  onClick: () => void;
}): JSX.Element => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={isSelected}
    className={`inline-flex items-center gap-1.5 rounded-(--radius-pill) border px-3 py-1.5 text-[12px] font-medium transition-colors ${
      isSelected
        ? "border-(--brand-primary) bg-(--pastel-violet) text-(--brand-primary)"
        : "border-dashed border-(--border-subtle) text-(--text-secondary) hover:border-(--brand-primary) hover:text-(--brand-primary)"
    }`}
  >
    {isSelected && order !== undefined && (
      <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-(--radius-pill) bg-(--brand-primary) px-1 text-[10px] font-bold text-(--brand-primary-contrast)">
        {order}
      </span>
    )}
    {label}
  </button>
);

type SectionProps = {
  config: AnalyticsConfig;
  onChange: (next: AnalyticsConfig) => void;
};

export const MetricsSection = ({
  config,
  onChange,
}: SectionProps): JSX.Element => {
  // Statuses and money are responses-only, and not while a counter is
  // selected: counters are a different grain and cannot share a row.
  const showRowMetrics =
    config.source === "responses" && config.counters.length === 0;

  return (
    <div className="flex flex-col gap-5">
      {config.source === "requests" && config.counters.length === 0 && (
        <p className="text-[12px] text-(--text-muted)">
          Ad requests report one metric: request count and distinct devices.
        </p>
      )}

      {showRowMetrics && (
        <div className="flex flex-col gap-2">
          <SectionTitle
            title="Statuses"
            hint="one column group each; selection order sets column order"
          />
          <div className="flex flex-wrap gap-2">
            {METRIC_STATUS_OPTIONS.map((opt) => {
              const order = config.statuses.indexOf(opt.value);
              return (
                <Chip
                  key={opt.value}
                  label={opt.label}
                  isSelected={order >= 0}
                  order={order + 1}
                  onClick={() => onChange(withStatusToggled(config, opt.value))}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Money (responses only — adRequest carries no prices) */}
      {showRowMetrics && (
        <div className="flex flex-col gap-2">
          <SectionTitle
            title="Money"
            hint="exact figures, matching the income board; ratios are per row"
          />
          <div className="flex flex-wrap gap-2">
            {MONEY_OPTIONS.map((opt) => (
              <Chip
                key={opt.value}
                label={opt.label}
                isSelected={config.money.includes(opt.value)}
                onClick={() => onChange(withMoneyToggled(config, opt.value))}
              />
            ))}
          </div>
        </div>
      )}

      {/* Exchange counters — the only source for no-bids, timeouts and
          throttles, and the cheapest source for request counts. */}
      <div className="flex flex-col gap-2">
        <SectionTitle
          title="Exchange counters"
          hint="per-minute aggregate; groups and filters by publisher or campaign only"
        />
        <div className="flex flex-wrap gap-2">
          {COUNTER_OPTIONS.map((opt) => (
            <Chip
              key={opt.value}
              label={opt.label}
              isSelected={config.counters.includes(opt.value)}
              onClick={() => onChange(withCounterToggled(config, opt.value))}
            />
          ))}
        </div>
        {config.counters.length > 0 && (
          <p className="text-[11px] text-(--text-muted)">
            Answered from the per-minute counters, so no request table is read.
            Grouping and filtering are limited to publisher and campaign.
          </p>
        )}
      </div>
    </div>
  );
};

// Shared by the Group by / Sort by / Columns sections: the properties the
// config can group by, in the same categories the Filters section uses.
const useConfigCategories = (config: AnalyticsConfig): AnalyticsCategory[] =>
  useMemo(
    () =>
      categoriesForConfig({ source: config.source, counters: config.counters }),
    [config.source, config.counters],
  );

const fieldName = (
  categories: AnalyticsCategory[],
  key: AnalyticsFieldKey,
): string => {
  const label = fieldLabelIn(categories, key);
  return label ? `${label.category} › ${label.field}` : FIELD_LABELS[key];
};

const fieldRow = (
  categories: AnalyticsCategory[],
  key: AnalyticsFieldKey,
): OrderedRow => ({
  key,
  name: fieldName(categories, key),
  label: (
    <FieldName
      categories={categories}
      fieldKey={key}
      fallback={FIELD_LABELS[key]}
    />
  ),
});

const TIME_ROW: OrderedRow = {
  key: "bucket",
  name: "Time",
  label: "Time",
  removable: false,
};

// Ordered list of groupings (drag to reorder, X to remove) and an empty line
// that adds one: Category → Property, red until both are chosen.
export const GroupingsSection = ({
  config,
  onChange,
}: SectionProps): JSX.Element => {
  const categories = useConfigCategories(config);
  // null until a draft was first opened; false once one closed, which hands
  // focus back to the "add" line.
  const [isAdding, setIsAdding] = useState<boolean | null>(null);
  const available = narrowCategories(
    categories,
    (key) => !config.groupBy.includes(key),
  );

  return (
    <div className="flex flex-col gap-2">
      {config.groupBy.length === 0 && isAdding !== true && (
        <p className="text-[12px] text-(--text-muted)">
          No grouping: one row per time bucket.
        </p>
      )}
      <OrderedRows
        ariaLabel="Groupings"
        rows={config.groupBy.map((key) => fieldRow(categories, key))}
        onMove={(from, to) => onChange(withFieldMoved(config, from, to))}
        onRemove={(key) =>
          onChange(withFieldToggled(config, key as AnalyticsFieldKey))
        }
      />
      {isAdding && available.length > 0 ? (
        <FieldPicker
          name="grouping"
          categories={available}
          onPick={(key) => {
            onChange(withFieldToggled(config, key));
            setIsAdding(false);
          }}
          onCancel={() => setIsAdding(false)}
        />
      ) : available.length > 0 ? (
        <AddRowButton
          label="Add grouping"
          autoFocus={isAdding === false}
          onClick={() => setIsAdding(true)}
        />
      ) : null}
    </div>
  );
};

// Row order: Time plus any grouping, ascending. Defaults to Time then every
// grouping in order; Time cannot be removed.
export const SortSection = ({
  config,
  onChange,
}: SectionProps): JSX.Element => {
  const categories = useConfigCategories(config);
  // null until a draft was first opened; false once one closed, which hands
  // focus back to the "add" line.
  const [isAdding, setIsAdding] = useState<boolean | null>(null);
  const sortBy = effectiveSortBy(config);
  const available = narrowCategories(
    categories,
    (key) => config.groupBy.includes(key) && !sortBy.includes(key),
  );

  return (
    <div className="flex flex-col gap-2">
      <OrderedRows
        ariaLabel="Sort order"
        rows={sortBy.map((key) =>
          key === "bucket" ? TIME_ROW : fieldRow(categories, key),
        )}
        onMove={(from, to) => onChange(withSortMoved(config, from, to))}
        onRemove={(key) =>
          onChange(withSortRemoved(config, key as AnalyticsSortKey))
        }
      />
      {isAdding && available.length > 0 ? (
        <FieldPicker
          name="sort key"
          categories={available}
          onPick={(key) => {
            onChange(withSortAdded(config, key));
            setIsAdding(false);
          }}
          onCancel={() => setIsAdding(false)}
        />
      ) : available.length > 0 ? (
        <AddRowButton
          label="Add sort key"
          autoFocus={isAdding === false}
          onClick={() => setIsAdding(true)}
        />
      ) : null}
      {config.groupBy.length === 0 && (
        <p className="text-[12px] text-(--text-muted)">
          Add a grouping to sort by it.
        </p>
      )}
    </div>
  );
};

// The table's columns: Time and the groupings (Time movable among them, never
// removed), then the metrics. Each block reorders within itself only; what is
// in them is chosen in Metrics and Group by.
export const ColumnsSection = ({
  config,
  onChange,
}: SectionProps): JSX.Element => {
  const categories = useConfigCategories(config);
  const metrics = metricGroupsFor(config);

  return (
    <div className="flex flex-col gap-3">
      <OrderedRows
        ariaLabel="Time and grouping columns"
        rows={dimensionColumns(config).map((key) =>
          key === "bucket" ? TIME_ROW : fieldRow(categories, key),
        )}
        onMove={(from, to) => onChange(withDimensionMoved(config, from, to))}
      />
      <OrderedRows
        ariaLabel="Metric columns"
        startNumber={2 + config.groupBy.length}
        rows={metrics.map((group) => ({
          key: group.key,
          name: group.label,
          label: group.label,
        }))}
        onMove={(from, to) => onChange(withMetricMoved(config, from, to))}
      />
    </div>
  );
};
